import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  History,
  Search,
  RotateCcw,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Action {
  id: string;
  kind: string;
  action: string;
  status: string;
  createdAt: string;
  projectId?: string;
  conversationId?: string;
  conversationTitle?: string;
  conversationScope?: string;
  conversationProjectId?: string;
  viewId?: string;
  taskId?: string;
  source?: string;
  canReverse?: boolean;
  canApprove?: boolean;
  runId?: string;
  reversalReason?: string;
  reversalStatus?: string;
  details: {
    input?: Record<string, unknown>;
    output?: Record<string, unknown>;
    error?: { message?: string };
    summary?: {
      destructive?: boolean;
      privateSourceDisclosure?: boolean;
      failure?: { message: string; failedIndex: number };
      previews?: {
        capability: string;
        references?: Record<string, string>;
        entityTitle: string;
        before?: Record<string, unknown>;
        after?: Record<string, unknown>;
      }[];
    };
    operations?: { entityId?: string; entityType?: string }[];
    restricted?: boolean;
    message?: string;
  };
}
const labels: Record<string, [string, string]> = {
  get_task: ["Lecture de la tâche", "Read task"],
  get_project: ["Lecture du projet", "Read project"],
  get_view: ["Lecture de la vue", "Read view"],
  list_projects: ["Lecture des projets accessibles", "Read accessible projects"],
  list_members: ["Lecture des membres", "Read members"],
  get_request_context: ["Contexte et permissions", "Context and permissions"],
  get_skill: ["Guide de l’application", "Application guide"],
  get_today_plan: ["Lecture du plan du jour", "Read today's plan"],
  get_document_version: ["Vérification du document", "Check document version"],
  propose_changes: ["Préparation des changements", "Prepare changes"],
  create_task: ["Création d’une tâche", "Create task"],
  update_task: ["Modification d’une tâche", "Update task"],
  archive_task: ["Archivage d’une tâche", "Archive task"],
  create_project: ["Création d’un projet", "Create project"],
  add_dependency: ["Ajout d’une dépendance", "Add dependency"], remove_dependency: ["Suppression d’une dépendance", "Remove dependency"],
  add_comment: ["Ajout d’un commentaire", "Add comment"], create_view: ["Création d’une vue", "Create view"], update_view: ["Modification d’une vue", "Update view"],
  create_custom_field: ["Création d’un champ personnalisé", "Create custom field"], set_task_assignees: ["Modification de l’assignation", "Set assignees"],
  "This batch contains an action without a safe inverse": ["L’annulation automatique n’est pas disponible pour ce type d’action.", "Automatic reversal is unavailable for this action type."],
  "Only the original actor may reverse this batch": ["Seule la personne à l’origine de l’action peut l’annuler.", "Only the original actor may reverse this batch."],
  "Only committed changes can be reversed": ["Seules les modifications appliquées peuvent être annulées.", "Only committed changes can be reversed."],
  "A reversal already exists": ["Une annulation existe déjà.", "A reversal already exists."],
  reversal_failed: ["Échec de l’annulation", "Reversal failed"],
  "Resource access is no longer available": ["Ressource inaccessible", "Resource unavailable"],
  get_document: ["Lecture du document", "Read document"],
  list_tasks: ["Lecture des tâches", "Read tasks"],
  search_tasks: ["Recherche de tâches", "Search tasks"],
  compact_context: ["Résumé du contexte", "Context compaction"],
  ask_user: ["Question", "Question"],
  request_reversal: ["Demande d’annulation", "Reversal requested"],
  undo_document_edit: ["Annulation du document", "Document reversal"],
  COMPLETED: ["Terminé", "Completed"],
  SUCCEEDED: ["Réussi", "Succeeded"],
  FAILED: ["Échec", "Failed"],
  CANCELLED: ["Annulé", "Cancelled"],
  PROPOSED: ["À approuver", "Proposed"],
  RUNNING: ["En cours", "Running"],
  SKIPPED: ["Ignoré", "Skipped"],
  APPLIED: ["Appliqué", "Applied"],
  UNDONE: ["Annulé", "Reversed"],
  WAITING_FOR_APPROVAL: ["À approuver", "Awaiting approval"],
  WAITING_FOR_INPUT: ["Réponse attendue", "Awaiting answer"],
  TODO: ["À faire", "To do"],
  IN_PROGRESS: ["En cours", "In progress"],
  DONE: ["Terminé", "Done"],
};
const fieldLabels: Record<string, [string, string]> = {
  title: ["Titre", "Title"], name: ["Nom", "Name"], description: ["Description", "Description"],
  status: ["Statut", "Status"], priority: ["Priorité", "Priority"], dueDate: ["Échéance", "Deadline"],
  startDate: ["Début", "Start"], endDate: ["Fin", "End"], assigneeIds: ["Assignation", "Assignees"],
  slotDate: ["Jour planifié", "Planned day"], durationMinutes: ["Durée (minutes)", "Duration (minutes)"],
  predecessorId: ["Tâche précédente", "Predecessor task"], successorId: ["Tâche suivante", "Successor task"], type: ["Type", "Type"],
  taskId: ["Tâche", "Task"], viewId: ["Vue", "View"], userIds: ["Membres", "Members"], options: ["Options", "Options"], config: ["Configuration", "Configuration"], customFields: ["Champs personnalisés", "Custom fields"],
  content: ["Contenu", "Content"], isShared: ["Partagé", "Shared"], isArchived: ["Archivé", "Archived"],
};
const internalFields = new Set(["id", "projectId", "createdAt", "updatedAt", "createdById", "userId", "version", "expectedVersion", "expectedUpdatedAt", "expectedSlotVersion", "sourceDocumentIds", "sourceEvidence", "requestId", "modelMessages", "assignees"]);
function Preview({ value, fields, projectId, references }: { value?: Record<string, unknown>; fields?: string[]; projectId?: string; references?: Record<string, string> }) {
  const fr = useI18n().language === "fr";
  if (!value) return <span className="text-foreground-muted">—</span>;
  const display = (key: string, item: unknown) => {
    if (item == null) return "—";
    if (projectId && ["taskId", "predecessorId", "successorId", "parentId"].includes(key) && typeof item === "string" && !item.startsWith("$")) return <a className="text-primary hover:underline" href={`/projects/${projectId}?task=${item}`}>{references?.[item] ?? item}</a>;
    if (["assigneeIds", "userIds"].includes(key) && Array.isArray(item)) return item.length ? `${item.length} ${fr ? "personne(s)" : "person(s)"}` : fr ? "Personne" : "None";
    if (typeof item === "boolean") return item ? fr ? "Oui" : "Yes" : fr ? "Non" : "No";
    if (typeof item === "string" && labels[item]) return labels[item]![fr ? 0 : 1];
    if (typeof item === "string" && /Date$/.test(key)) { const date = new Date(item); if (!isNaN(date.getTime())) return date.toLocaleDateString(fr ? "fr" : "en"); }
    return typeof item === "object" ? JSON.stringify(item, null, 2) : String(item);
  };
  return (
    <dl className="space-y-1 text-xs">
      {Object.entries(value)
        .filter(
          ([key]) => !internalFields.has(key) && (!fields || fields.includes(key)),
        )
        .map(([key, item]) => (
          <div key={key} className="flex gap-2">
            <dt className="text-foreground-muted min-w-20">{fieldLabels[key]?.[fr ? 0 : 1] ?? key.replace(/([A-Z])/g, " $1")}</dt>
            <dd className="min-w-0 whitespace-pre-wrap break-words">
              {display(key, item)}
            </dd>
          </div>
        ))}
    </dl>
  );
}
export function AiActivity({ conversationId, projectId }: { conversationId?: string; projectId?: string }) {
  const fr = useI18n().language === "fr";
  const client = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [offset, setOffset] = useState(0);
  const [project, setProject] = useState(projectId ?? "");
  const [selectedChat, setSelectedChat] = useState("");
  const filters = useQuery({
    queryKey: ["ai-history-filters"],
    queryFn: () =>
      apiGet<{
        projects: { id: string; name: string }[];
        conversations: { id: string; title: string | null }[];
      }>("/ai/history/filters"),
    enabled: !conversationId,
  });
  const [confirm, setConfirm] = useState<Action | null>(null);
  const key = [
    "ai-history",
    conversationId,
    search,
    status,
    source,
    project,
    selectedChat,
    offset,
  ];
  const history = useQuery({
    queryKey: key,
    queryFn: () =>
      apiGet<{ items: Action[]; hasMore: boolean }>(
        `/ai/history?${new URLSearchParams({ ...(conversationId || selectedChat ? { conversationId: conversationId || selectedChat } : {}), ...(project ? { projectId: project } : {}), ...(search ? { search } : {}), ...(status ? { status } : {}), ...(source ? { source } : {}), offset: String(offset), limit: "15" })}`,
      ),
    refetchInterval: 4000,
  });
  const reverse = useMutation({
    mutationFn: (item: Action) =>
      apiPost(
        item.kind === "document"
          ? `/ai/document-changes/${item.id}/undo`
          : `/ai/changes/${item.id}/undo`,
        {},
      ),
    onSuccess: () => {
      setConfirm(null);
      void client.invalidateQueries({ queryKey: ["ai-history"] });
      void client.invalidateQueries({ queryKey: ["ai-chat"] });
    },
  });
  const approve = useMutation({
    mutationFn: (item: Action) => apiPost(`/ai/changes/${item.id}/approve`, {}),
    onSuccess: () => { setConfirm(null); void client.invalidateQueries({ queryKey: ["ai-history"] }); void client.invalidateQueries({ queryKey: ["ai-chat"] }); },
  });
  const label = (value: string) =>
    labels[value]?.[fr ? 0 : 1] ?? value.replace(/_/g, " ");
  return (
    <div className="space-y-4 min-w-0">
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-40">
          <Search className="absolute left-3 top-3 h-4 w-4 text-foreground-muted" />
          <Input
            className="pl-9"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setOffset(0);
            }}
            placeholder={fr ? "Rechercher une action…" : "Search actions…"}
            aria-label={fr ? "Rechercher dans l’historique" : "Search history"}
          />
        </div>
        <select
          aria-label={fr ? "Statut" : "Status"}
          className="rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setOffset(0);
          }}
        >
          <option value="">{fr ? "Tous les statuts" : "All statuses"}</option>
          {[
            "COMPLETED",
            "FAILED",
            "CANCELLED",
            "WAITING_FOR_APPROVAL",
            "RUNNING",
          ].map((value) => (
            <option key={value} value={value}>
              {label(value)}
            </option>
          ))}
        </select>
        {!conversationId && (
          <select
            aria-label={fr ? "Source" : "Source"}
            className="rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm"
            value={source}
            onChange={(event) => {
              setSource(event.target.value);
              setOffset(0);
            }}
          >
            <option value="">
              {fr ? "Toutes les sources" : "All sources"}
            </option>
            {["chat", "discord", "automation", "mcp"].map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        )}
      </div>
      {!conversationId && (
        <div className="flex flex-wrap gap-2">
          <select
            aria-label={fr ? "Projet" : "Project"}
            className="rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm"
            value={project}
            onChange={(event) => {
              setProject(event.target.value);
              setOffset(0);
            }}
          >
            <option value="">{fr ? "Tous les projets" : "All projects"}</option>
            {filters.data?.projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <select
            aria-label={fr ? "Conversation" : "Conversation"}
            className="max-w-full rounded-lg border border-white/10 bg-surface px-3 py-2 text-sm"
            value={selectedChat}
            onChange={(event) => {
              setSelectedChat(event.target.value);
              setOffset(0);
            }}
          >
            <option value="">
              {fr ? "Toutes les conversations" : "All conversations"}
            </option>
            {filters.data?.conversations.map((chat) => (
              <option key={chat.id} value={chat.id}>
                {chat.title ?? "Conversation"}
              </option>
            ))}
          </select>
        </div>
      )}
      {history.isPending && (
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      )}
      {history.error && (
        <p role="alert" className="text-sm text-destructive">
          {history.error.message}
        </p>
      )}
      {history.data?.items.length === 0 && (
        <p className="py-12 text-center text-foreground-muted">
          {fr
            ? "Aucune action pour ces filtres."
            : "No actions match these filters."}
        </p>
      )}
      <div className="space-y-3">
        {history.data?.items.map((item) => (
          <article
            key={`${item.kind}:${item.id}`}
            className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3"
          >
            <div className="flex items-start gap-3">
              <History className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="font-medium break-words">{label(item.action)}</p>
                <p className="text-xs text-foreground-muted">
                  {new Date(item.createdAt).toLocaleString(fr ? "fr" : "en")}{" "}
                  {item.source && `· ${item.source}`}{" "}
                  {!conversationId &&
                    item.conversationTitle &&
                    `· ${item.conversationTitle}`}
                </p>
              </div>
              <span
                className={`shrink-0 rounded-md px-2 py-1 text-xs ${item.status === "FAILED" ? "bg-red-400/10 text-red-300" : "bg-primary/10 text-primary"}`}
              >
                {label(item.status)}
              </span>
            </div>
            {(item.details.error?.message || item.details.summary?.failure) && (
              <p
                role="alert"
                className="rounded-lg bg-red-400/5 p-3 text-sm text-red-300"
              >
                {item.details.summary?.failure
                  ? `${fr ? "Opération" : "Operation"} ${item.details.summary.failure.failedIndex + 1}: ${item.details.summary.failure.message}`
                  : item.details.error?.message}
              </p>
            )}
            {item.details.summary?.previews?.map((preview, index) => (
              <details
                key={index}
                className="rounded-lg border border-white/5 p-3"
              >
                <summary className="cursor-pointer text-sm">
                  {preview.entityTitle || label(preview.capability)}
                </summary>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="mb-2 text-xs text-foreground-muted">
                      {fr ? "Avant" : "Before"}
                    </p>
                    <Preview value={preview.before} fields={preview.after ? Object.keys(preview.after) : undefined} projectId={item.projectId} references={preview.references} />
                  </div>
                  <div>
                    <p className="mb-2 text-xs text-foreground-muted">
                      {fr ? "Après" : "After"}
                    </p>
                    <Preview value={preview.after} projectId={item.projectId} references={preview.references} />
                  </div>
                </div>
              </details>
            ))}
            {item.kind === "execution" && !item.details.restricted && (
              <div className="text-sm text-foreground-secondary break-words">
                {Array.isArray(item.details.output?.tasks) && item.details.output.tasks.length ? (
                  <ul className="space-y-2">
                    {item.details.output.tasks.map((task: { id: string; projectId: string; title: string }) => (
                      <li key={task.id}>
                        <a className="inline-flex items-start gap-2 text-primary hover:underline" href={`/projects/${task.projectId}?task=${task.id}`}>
                          <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0" />{task.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : <ReactMarkdown>{String(item.details.output?.message ?? "")}</ReactMarkdown>}
              </div>
            )}
            {item.kind === "step" &&
              !item.details.restricted &&
              typeof item.details.output?.name === "string" && (
                <p className="text-sm">{String(item.details.output.name)}</p>
              )}
            {item.details.restricted && (
              <p className="text-sm text-foreground-muted">
                {fr
                  ? "Vous n’avez plus accès à cette ressource."
                  : "Resource access is no longer available."}
              </p>
            )}
            {item.kind === "step" && !item.details.restricted && (item.details.input || item.details.output) && <details className="rounded-lg border border-white/5 p-3"><summary className="cursor-pointer text-sm">{fr ? "Détails de l’action" : "Action details"}</summary><div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4"><Preview value={item.details.input} projectId={item.projectId} /><Preview value={item.details.output} projectId={item.projectId} /></div></details>}
            <div className="flex flex-wrap items-center gap-3">
              {item.canApprove && <Button size="sm" onClick={() => setConfirm(item)}>{fr ? "Examiner et approuver" : "Review and approve"}</Button>}
              {!conversationId && item.conversationId && (
                <a
                  className="text-xs text-primary"
                  href={
                    item.conversationScope === "PROJECT" && item.conversationProjectId
                      ? `/projects/${item.conversationProjectId}?aiConversation=${item.conversationId}`
                      : `/assistant?conversation=${item.conversationId}`
                  }
                >
                  {fr ? "Ouvrir la conversation" : "Open conversation"}
                </a>
              )}
              {item.details.operations
                ?.filter((op) => op.entityId && op.entityType === "task")
                .map((op) => (
                  <a
                    key={op.entityId}
                    className="inline-flex items-center gap-1 text-xs text-primary"
                    href={`/projects/${item.projectId}?task=${op.entityId}`}
                  >
                    <ExternalLink className="h-3 w-3" />
                    {fr ? "Ouvrir la tâche" : "Open task"}
                  </a>
                ))}
              {item.taskId && (
                <a
                  className="text-xs text-primary"
                  href={`/projects/${item.projectId}?task=${item.taskId}`}
                >
                  {fr ? "Ouvrir la tâche" : "Open task"}
                </a>
              )}
              {item.viewId && (
                <a
                  className="text-xs text-primary"
                  href={`/projects/${item.projectId}?view=${item.viewId}`}
                >
                  {fr ? "Ouvrir le document" : "Open document"}
                </a>
              )}
              {item.canReverse ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirm(item)}
                >
                  <RotateCcw className="mr-2 h-3 w-3" />
                  {fr ? "Annuler cette action" : "Reverse action"}
                </Button>
              ) : (
                item.kind === "changeSet" && (
                  <span className="text-xs text-foreground-muted">
                    {item.reversalReason ? label(item.reversalReason) : ""}
                  </span>
                )
              )}
            </div>
          </article>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          disabled={offset === 0}
          onClick={() => setOffset((value) => Math.max(0, value - 15))}
        >
          {fr ? "Précédent" : "Previous"}
        </Button>
        <Button
          variant="ghost"
          disabled={!history.data?.hasMore}
          onClick={() => setOffset((value) => value + 15)}
        >
          {fr ? "Suivant" : "Next"}
        </Button>
      </div>
      <Dialog
        open={!!confirm}
        onOpenChange={(open) => {
          if (!open && !reverse.isPending && !approve.isPending) setConfirm(null);
        }}
      >
        <DialogContent className="max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {confirm?.canApprove ? (fr ? "Appliquer ces modifications ?" : "Apply these changes?") : (fr ? "Annuler cette action ?" : "Reverse this action?")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground-secondary">{confirm?.action}</p>
          <p className="text-sm text-foreground-muted">
            {confirm?.canApprove ? (fr ? "Les permissions et versions seront vérifiées. Le lot sera appliqué entièrement, ou aucune modification ne sera appliquée." : "Permissions and versions will be checked. The entire batch will apply, or no changes will apply.") : fr
              ? "Les permissions et versions seront vérifiées. Un lot est annulé entièrement ou aucune modification n’est appliquée."
              : "Permissions and versions will be checked. A batch is reversed entirely or no changes are applied."}
          </p>
          {confirm?.canApprove && confirm.details.summary?.destructive && <p className="text-sm text-amber-400">{fr ? "Ce lot contient des modifications destructives." : "This batch contains destructive changes."}</p>}
          {confirm?.canApprove && confirm.details.summary?.privateSourceDisclosure && <p className="text-sm text-amber-400">{fr ? "Ces modifications peuvent partager du contenu issu de documents privés." : "These changes may disclose content from private documents."}</p>}
          {confirm?.canApprove && confirm.details.summary?.previews?.map((preview, index) => <div key={index} className="rounded-lg border border-white/10 p-3"><p className="text-sm mb-2">{preview.entityTitle || label(preview.capability)}</p><Preview value={preview.after} projectId={confirm.projectId} references={preview.references} /></div>)}
          {(reverse.error || approve.error) && (
            <p role="alert" className="text-sm text-destructive">
              {(reverse.error || approve.error)?.message}
            </p>
          )}
          <Button
            disabled={reverse.isPending || approve.isPending}
            onClick={() => {
              if (confirm) { if (confirm.canApprove) approve.mutate(confirm); else reverse.mutate(confirm); }
            }}
          >
            {(reverse.isPending || approve.isPending) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {confirm?.canApprove ? (fr ? "Appliquer" : "Apply") : (fr ? "Préparer l’annulation" : "Prepare reversal")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
export function AiHistoryDialog({
  conversationId,
  open,
  onClose,
  projectId,
}: {
  conversationId?: string;
  projectId?: string;
  open: boolean;
  onClose(): void;
}) {
  const fr = useI18n().language === "fr";
  const [projectHistory, setProjectHistory] = useState(!conversationId);
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent className="max-w-3xl max-h-[85dvh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {fr ? "Historique des actions" : "Action history"}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-0 overflow-y-auto">
          {projectId && conversationId && <div className="flex gap-2 mb-4"><Button size="sm" variant={!projectHistory ? "default" : "outline"} onClick={() => setProjectHistory(false)}>{fr ? "Cette conversation" : "This conversation"}</Button><Button size="sm" variant={projectHistory ? "default" : "outline"} onClick={() => setProjectHistory(true)}>{fr ? "Tout le projet / MCP" : "Whole project / MCP"}</Button></div>}
          {open && <AiActivity key={projectHistory ? projectId : conversationId} conversationId={projectHistory ? undefined : conversationId} projectId={projectHistory ? projectId : undefined} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
